import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, TextField, Button, Radio, RadioGroup,
  FormControlLabel, FormControl, Checkbox, IconButton, Divider,
  InputAdornment, CircularProgress, Alert, Dialog, DialogTitle,
  DialogContent, DialogActions, Chip, Tooltip, Fade, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Select, MenuItem,
} from '@mui/material';
import {
  Search as SearchIcon, Save as SaveIcon, Clear as ClearIcon,
  CloudDownload, Refresh as RefreshIcon, Add as AddIcon, Close as CloseIcon,
  HealthAndSafety as InsuranceIcon, LocalHospital as ProcedureIcon,
  CheckCircle as CheckCircleIcon, Delete as DeleteIcon, ContentCopy as CopyIcon,
  //  The pencil beside the procedure name.
  Edit as EditIcon,
  //  "Can a claim actually see this?" — the check beside the pencil.
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import axios from 'axios';
import Swal from 'sweetalert2';

import {
  loadInsurance, loadProcedures, getProcedureInsurances,
  updateInsuredProcedure, deleteInsuredProcedure, saveNewProcedure,
  bulkApplyProcedureInsurance,
  //  Renames the procedure across every insurance record filed under it.
  renameProcedure,
  //  Runs the claims picker against one catalogue entry and reports whether it
  //  would ever be offered — see checkClaimsSide below.
  catalogueVisibility,
} from '../BaseURL';
import {
  AdminPageHead, ADMIN, EmptyState, BulkEditBar, adminFieldSx,
  brandButtonSx, brandGhostButtonSx,
} from './adminUi';

// The bulk-edit bar's fields. Blank means "leave this column alone", so the
// starting state is all-blank and pressing Apply with nothing typed is a no-op.
//
// ICD-10 IS here, and the comment that used to say otherwise was wrong. It read:
// "that code identifies a diagnosis, not a procedure. A procedure is billed on its
// G_DRG." The billing half is true and the conclusion did not follow — the NHIS
// procedure sheet lists every procedure against the ICD-10 codes it is INDICATED
// for, one row each, and that is what `procedure_tarf.icd` (migration 042) holds.
// Without the field the codes imported and then could not be seen or corrected.
const EMPTY_BULK = { tariff: '', topup: '', G_DRG: '', icd: '' };

const BULK_FIELDS = [
  { key: 'tariff', label: 'Tariff',    type: 'number', adornment: 'GH¢' },
  { key: 'topup',  label: 'Top-up',    type: 'number', adornment: 'GH¢' },
  { key: 'G_DRG',  label: 'GDRG Code', placeholder: 'e.g. SURG12B' },
  { key: 'icd',    label: 'ICD-10',    placeholder: 'e.g. N80' },
];

//  The same section label Diagnosis Setup uses: a green accent bar, an
//  uppercase caption and a rule to the right, turning red when the section
//  has an error.
const PxSectionLabel = ({ children, error }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.5 }}>
    <Box sx={{ width: 3, height: 14, borderRadius: 1, background: error ? '#D64545' : '#2E9E4F', flexShrink: 0 }} />
    <Typography sx={{
      fontSize: 11, fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase',
      color: error ? '#D64545' : '#5A6B87', whiteSpace: 'nowrap',
    }}>
      {children}
    </Typography>
    <Box sx={{ flex: 1, height: '1px', background: '#DDE5EF' }} />
  </Box>
);

//  Selected choice is FILLED, not outlined — an outline alone is what MUI also
//  uses for focus, so the tile you tabbed to looked like the one you picked.
const pxChoiceSx = (on) => ({
  m: 0,
  px: 1.25, py: .625,
  borderRadius: '8px',
  border: `1px solid ${on ? '#2E9E4F' : '#DDE5EF'}`,
  background: on ? '#2E9E4F' : '#fff',
  color: on ? '#fff' : '#5A6B87',
  transition: 'border-color .15s, background .15s',
});

/* ─────────────────────────────────────────────────────────────
   Shared compact styles
───────────────────────────────────────────────────────────── */
const cellSelectSx = {
  fontSize: '0.78rem',
  '& .MuiSelect-select': { py: '4px', px: '8px' },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#DDE5EF' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#2E9E4F' },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#2E9E4F' },
};
const cellInputSx = {
  '& .MuiOutlinedInput-root': {
    fontSize: '0.78rem',
    '& input': { py: '4px', px: '8px' },
    '& fieldset': { borderColor: '#DDE5EF' },
    '&:hover fieldset': { borderColor: '#2E9E4F' },
    '&.Mui-focused fieldset': { borderColor: '#2E9E4F' },
  },
};

/* ─────────────────────────────────────────────────────────────
   Editable table row with checkbox selection
───────────────────────────────────────────────────────────── */
const EditableRow = ({ row, idx, selected, onSelectToggle, onChange, onUpdate, onDelete, updating, deleting }) => {
  const tdSx = { borderBottom: '1px solid #DDE5EF', py: 0.75, px: 1 };
  return (
    <TableRow sx={{
      '&:nth-of-type(odd)': { background: selected ? 'rgba(18,58,109,0.12)' : '#f9faff' },
      '&:nth-of-type(even)': { background: selected ? 'rgba(18,58,109,0.12)' : '#fff' },
      '&:hover': { background: selected ? 'rgba(18,58,109,0.18)' : 'rgba(18,58,109,0.04)' },
      transition: 'background 0.2s',
    }}>
      <TableCell sx={{ ...tdSx, width: 48, textAlign: 'center', px: 0.5 }}>
        <Checkbox 
          size="small"
          checked={selected}
          onChange={() => onSelectToggle(row.id)}
          sx={{ 
            color: '#2E9E4F', 
            '&.Mui-checked': { color: '#2E9E4F' },
            p: 0.5
          }}
        />
      </TableCell>
      <TableCell sx={{ ...tdSx, width: 36, textAlign: 'center', color: '#93A2B8', fontSize: '0.72rem', fontWeight: 500 }}>{idx + 1}</TableCell>
      <TableCell sx={{ ...tdSx, minWidth: 130 }}>
        <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#0F1F3D' }}>{row.status || '—'}</Typography>
      </TableCell>
      <TableCell sx={{ ...tdSx, minWidth: 100 }}>
        <TextField size="small" type="number" fullWidth value={row.tariff ?? ''}
          onChange={e => { const v = e.target.value; if (v === '' || parseFloat(v) >= 0) onChange('tariff', v); }}
          inputProps={{ min: 0, step: '0.01' }} sx={cellInputSx} />
      </TableCell>
      <TableCell sx={{ ...tdSx, minWidth: 95 }}>
        <TextField size="small" type="number" fullWidth value={row.topup ?? ''}
          onChange={e => { const v = e.target.value; if (v === '' || parseFloat(v) >= 0) onChange('topup', v); }}
          inputProps={{ min: 0, step: '0.01' }} sx={cellInputSx} />
      </TableCell>
      <TableCell sx={{ ...tdSx, minWidth: 100 }}>
        <Select size="small" fullWidth value={row.age || ''} onChange={e => onChange('age', e.target.value)} displayEmpty sx={cellSelectSx}>
          <MenuItem value="" disabled><em style={{ color: '#bbb', fontSize: '0.75rem' }}>Select…</em></MenuItem>
          <MenuItem value="Adult">Adult</MenuItem>
          <MenuItem value="Child">Child</MenuItem>
          <MenuItem value="Both">Both</MenuItem>
        </Select>
      </TableCell>
      <TableCell sx={{ ...tdSx, minWidth: 110 }}>
        <TextField size="small" fullWidth value={row.G_DRG ?? ''} onChange={e => onChange('G_DRG', e.target.value)} sx={cellInputSx} />
      </TableCell>
      {/* The ICD-10 the procedure is indicated for. Next to the G-DRG because the
          two together are what identify the row: the NHIS sheet lists one
          procedure against many ICD-10 codes, so OBGY01 occupies one row per
          code and the codes are the only thing telling them apart. */}
      <TableCell sx={{ ...tdSx, minWidth: 100 }}>
        <TextField size="small" fullWidth value={row.icd ?? ''}
          onChange={e => onChange('icd', e.target.value)}
          placeholder="e.g. N80" sx={cellInputSx} />
      </TableCell>
      {/*  SPECIALTY, with its code underneath rather than in a column of its own.
           The code had a 90px column that pushed Actions off the right edge, and
           it is the one field on this row nobody types: it is the first four
           letters of the G-DRG (ORTH27 → ORTH) and arrives filled in from the
           catalogue. Shown, so the row still says what it is; not given a column,
           so the row fits. It is still STORED and still sent on every update —
           see handleUpdateRow — and still editable in the New Procedure dialog,
           which is the one place it is ever actually set. */}
      <TableCell sx={{ ...tdSx, minWidth: 130 }}>
        <TextField size="small" fullWidth value={row.spe ?? ''} onChange={e => onChange('spe', e.target.value)} sx={cellInputSx} />
        {row.spe_code && (
          <Typography sx={{ fontSize: 10, color: '#93A2B8', mt: .25, pl: .25, letterSpacing: '.03em' }}>
            {row.spe_code}
          </Typography>
        )}
      </TableCell>
      {/*  Icon buttons. Two labelled buttons needed 180px and were the other half
           of why Actions sat past the right edge. The tooltip and the aria-label
           carry the word, so nothing is lost to anyone reading the screen or a
           screen reader. */}
      <TableCell sx={{ ...tdSx, width: 96, textAlign: 'center' }}>
        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
          <Tooltip title="Save this row" arrow>
            <span>
              <IconButton size="small" onClick={() => onUpdate(row)} disabled={updating || deleting}
                aria-label={`Save ${row.status || 'this scheme'}`}
                sx={{ color: '#fff', background: '#217A3C', borderRadius: '7px', p: .6,
                  '&:hover': { background: '#1B6631' }, '&:disabled': { background: '#DDE5EF', color: '#93A2B8' } }}>
                {updating ? <CircularProgress size={14} color="inherit" /> : <SaveIcon sx={{ fontSize: 16 }} />}
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Delete this row" arrow>
            <span>
              <IconButton size="small" onClick={() => onDelete(row)} disabled={deleting || updating}
                aria-label={`Delete ${row.status || 'this scheme'}`}
                sx={{ color: '#fff', background: '#D64545', borderRadius: '7px', p: .6,
                  '&:hover': { background: '#B23A3A' }, '&:disabled': { background: '#DDE5EF', color: '#93A2B8' } }}>
                {deleting ? <CircularProgress size={14} color="inherit" /> : <DeleteIcon sx={{ fontSize: 16 }} />}
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </TableCell>
    </TableRow>
  );
};

/* ─────────────────────────────────────────────────────────────
   Bulk Apply Dialog - Apply selected row to multiple insurances
───────────────────────────────────────────────────────────── */
const BulkApplyDialog = ({ open, onClose, sourceRow, insuranceProviders, loadingInsurance, onSaved, existingStatuses }) => {
  const [saving, setSaving] = useState(false);
  const [selectedInsurances, setSelectedInsurances] = useState({});
  const [insuranceSearch, setInsuranceSearch] = useState('');

  const selectedInsuranceIds = Object.keys(selectedInsurances).filter(id => selectedInsurances[id]);
  const selectedCount = selectedInsuranceIds.length;

  useEffect(() => {
    if (open) {
      setSelectedInsurances({});
      setInsuranceSearch('');
    }
  }, [open]);

  const handleInsuranceToggle = (id) => {
    setSelectedInsurances(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleSelectAll = () => {
    const all = {};
    insuranceProviders.forEach(i => { 
      if (!existingStatuses.includes(i.name)) {
        all[i.id] = true; 
      }
    });
    setSelectedInsurances(all);
  };

  const handleDeselectAll = () => setSelectedInsurances({});

  const handleApply = async () => {
    if (selectedCount === 0) {
      await Swal.fire({
        title: 'No Selection',
        text: 'Please select at least one insurance provider to apply this procedure to.',
        icon: 'warning',
        confirmButtonColor: '#217A3C',
      });
      return;
    }

    setSaving(true);
    try {
      const rows = selectedInsuranceIds.map(id => {
        const provider = insuranceProviders.find(p => String(p.id) === String(id));
        return {
          age: sourceRow.age,
          procedure_: sourceRow.procedure_,
          procedure_group: sourceRow.procedure_group,
          G_DRG: sourceRow.G_DRG,
          // Carried onto the new insurer's row: the ICD-10 a procedure is
          // indicated for comes from NHIS and does not vary by insurer.
          icd: sourceRow.icd || null,
          tariff: parseFloat(sourceRow.tariff),
          topup: parseFloat(sourceRow.topup),
          spe: sourceRow.spe,
          spe_code: sourceRow.spe_code,
          status: provider?.name || ''
        };
      });

      const response = await axios.post(bulkApplyProcedureInsurance, { rows });

      if (response.data.success) {
        await Swal.fire({
          title: 'Applied!',
          html: `<b>${sourceRow.procedure_}</b> applied to <b>${selectedCount}</b> insurance provider${selectedCount !== 1 ? 's' : ''}.`,
          icon: 'success',
          timer: 2500,
          showConfirmButton: false,
          timerProgressBar: true,
        });
        if (onSaved) onSaved();
        handleClose();
      } else {
        throw new Error(response.data.message || 'Apply failed');
      }
    } catch (err) {
      await Swal.fire({
        title: 'Apply Failed',
        text: err.response?.data?.message || err.message || 'An error occurred while applying',
        icon: 'error',
        confirmButtonColor: '#D64545',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setSelectedInsurances({});
    setInsuranceSearch('');
    onClose();
  };

  const filteredProviders = insuranceProviders.filter(i =>
    i.name?.toLowerCase().includes(insuranceSearch.toLowerCase())
  );

  const availableProviders = filteredProviders.filter(i => !existingStatuses.includes(i.name));
  const alreadyAssignedProviders = filteredProviders.filter(i => existingStatuses.includes(i.name));

  if (!sourceRow) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth TransitionComponent={Fade}
      PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden', boxShadow: '0 24px 64px rgba(0,0,0,0.18)' } }}>

      <DialogTitle sx={{ background: 'linear-gradient(135deg,#123A6D,#0B2452)', color: 'white', p: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <CopyIcon sx={{ fontSize: 28 }} />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1 }}>Apply to Insurance Providers</Typography>
            <Typography variant="caption" sx={{ opacity: 0.85 }}>Select providers to apply this procedure</Typography>
          </Box>
        </Box>
        <IconButton onClick={handleClose} sx={{ color: 'white' }}><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {/* Source Row Info */}
        {/* Source row summary. Was a 7-cell MUI grid of bold-label pairs — and
            those cells carried no `size` prop at all, so MUI 7 ignored every one
            and collapsed the lot into a single column of run-together lines. A
            definition grid now: label above value, so it scans as a record.
            Same fix, same shape, as the one on Diagnosis Setup. */}
        <Paper elevation={0} sx={{ p: 2, mb: 2.5, background: '#fff', border: '1px solid #DDE5EF', borderRadius: ADMIN.radiusSm }}>
          <PxSectionLabel>Copying from</PxSectionLabel>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#0F1F3D', mb: 1.5 }}>
            {sourceRow.procedure_}
          </Typography>
          <Box sx={{
            display: 'grid', gap: 1.5,
            gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(3, minmax(0, 1fr))' },
          }}>
            {[
              ['Group',     sourceRow.procedure_group],
              ['DRG',       sourceRow.G_DRG],
              ['Specialty', sourceRow.spe_code],
              ['Tariff',    sourceRow.tariff != null ? `GH¢ ${sourceRow.tariff}` : null],
              ['Top-up',    sourceRow.topup  != null ? `GH¢ ${sourceRow.topup}`  : null],
              ['Applies to', sourceRow.age],
            ].map(([label, value]) => (
              <Box key={label} sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 9.5, fontWeight: 800, letterSpacing: '.8px', textTransform: 'uppercase', color: '#93A2B8' }}>
                  {label}
                </Typography>
                <Typography noWrap title={value || '—'} sx={{ fontSize: 12.5, fontWeight: 600, color: value ? '#0F1F3D' : '#93A2B8' }}>
                  {value || '—'}
                </Typography>
              </Box>
            ))}
          </Box>
        </Paper>

        {/* Insurance Selection */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Box>
            <PxSectionLabel>Select Insurance Providers</PxSectionLabel>
            <Typography variant="caption" color="text.secondary">
              {selectedCount} of {availableProviders.length} available selected
            </Typography>
          </Box>
          {selectedCount > 0 && (
            <Chip size="small" icon={<CheckCircleIcon sx={{ fontSize: '14px !important' }} />}
              label={`${selectedCount} selected`}
              sx={{ background: '#E7F5EC', color: '#217A3C', fontWeight: 600, fontSize: '0.7rem' }} />
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
          <Button size="small" variant="outlined" onClick={handleSelectAll}
            sx={{ borderColor: '#2E9E4F', color: '#2E9E4F', fontSize: '0.72rem', py: 0.4 }}>
            Select All Available
          </Button>
          <Button size="small" variant="outlined" onClick={handleDeselectAll}
            sx={{ borderColor: '#2E9E4F', color: '#2E9E4F', fontSize: '0.72rem', py: 0.4 }}>
            Deselect All
          </Button>
        </Box>

        <TextField fullWidth size="small" placeholder="Search insurance..."
          value={insuranceSearch} onChange={e => setInsuranceSearch(e.target.value)} sx={{ mb: 1.5 }}
          InputProps={{ endAdornment: (<InputAdornment position="end"><SearchIcon sx={{ fontSize: 18, color: '#93A2B8' }} /></InputAdornment>) }} />

        <Box sx={{
          border: '1px solid #F4F7FB', borderRadius: 2, maxHeight: 320, overflowY: 'auto',
          '&::-webkit-scrollbar': { width: '6px' },
          '&::-webkit-scrollbar-thumb': { background: '#DDE5EF', borderRadius: '10px' },
        }}>
          {loadingInsurance ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <>
              {/* Available Providers */}
              {availableProviders.length > 0 && (
                <Box sx={{ p: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#217A3C', display: 'block', mb: 0.5, px: 1 }}>
                    Available ({availableProviders.length})
                  </Typography>
                  {availableProviders.map(insurance => (
                    <Box key={insurance.id} onClick={() => handleInsuranceToggle(insurance.id)}
                      sx={{
                        display: 'flex', alignItems: 'center', p: '4px 8px', borderRadius: 1,
                        cursor: 'pointer', mb: 0.5,
                        background: selectedInsurances[insurance.id] ? 'rgba(18,58,109,0.06)' : 'transparent',
                        border: selectedInsurances[insurance.id] ? '1px solid rgba(18,58,109,0.22)' : '1px solid transparent',
                        transition: 'all 0.15s ease',
                        '&:hover': { background: 'rgba(18,58,109,0.05)' },
                      }}>
                      <Checkbox size="small"
                        checked={selectedInsurances[insurance.id] || false}
                        onChange={() => handleInsuranceToggle(insurance.id)}
                        onClick={e => e.stopPropagation()}
                        sx={{ p: 0.5, color: '#2E9E4F', '&.Mui-checked': { color: '#2E9E4F' } }} />
                      <Typography variant="body2" sx={{ ml: 0.5, fontSize: '0.82rem', color: '#0F1F3D' }}>
                        {insurance.name}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}

              {/* Already Assigned Providers */}
              {alreadyAssignedProviders.length > 0 && (
                <Box sx={{ p: 1, borderTop: availableProviders.length > 0 ? '1px solid #DDE5EF' : 'none' }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#D64545', display: 'block', mb: 0.5, px: 1 }}>
                    Already Assigned ({alreadyAssignedProviders.length})
                  </Typography>
                  {alreadyAssignedProviders.map(insurance => (
                    <Box key={insurance.id}
                      sx={{
                        display: 'flex', alignItems: 'center', p: '4px 8px', borderRadius: 1,
                        mb: 0.5, background: 'rgba(214,69,69,0.05)', opacity: 0.6,
                        border: '1px solid rgba(214,69,69,0.1)',
                      }}>
                      <Checkbox size="small" disabled checked={false}
                        sx={{ p: 0.5 }} />
                      <Typography variant="body2" sx={{ ml: 0.5, fontSize: '0.82rem', color: '#5A6B87' }}>
                        {insurance.name}
                      </Typography>
                      <Chip label="Exists" size="small" sx={{ ml: 'auto', fontSize: '0.65rem', height: 18, background: '#FDECEC', color: '#B23A3A' }} />
                    </Box>
                  ))}
                </Box>
              )}

              {availableProviders.length === 0 && alreadyAssignedProviders.length === 0 && (
                <EmptyState
                    icon={InsuranceIcon}
                    title="No providers"
                    body="No insurance scheme matched."
                  />
              )}
            </>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: '1px solid #DDE5EF', background: '#F4F7FB', gap: 1 }}>
        <Button onClick={handleClose} startIcon={<ClearIcon />} variant="outlined" disabled={saving}
          sx={brandGhostButtonSx}>
          Cancel
        </Button>
        <Button variant="contained"
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <CopyIcon />}
          onClick={handleApply} disabled={saving || selectedCount === 0}
          sx={{ ...brandButtonSx, px: 3, '&:disabled': { background: '#DDE5EF', color: '#93A2B8' } }}>
          {saving ? 'Applying...' : `Apply to ${selectedCount} Provider${selectedCount !== 1 ? 's' : ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/* ─────────────────────────────────────────────────────────────
   New-Procedure Dialog
───────────────────────────────────────────────────────────── */
const NewProcedureDialog = ({ open, onClose, insuranceProviders, loadingInsurance, onSaved }) => {
  const [saving, setSaving] = useState(false);

  // ── form state ────────────────────────────────────────────
  const [ageCategory, setAgeCategory] = useState('');
  const [procedureName, setProcedureName] = useState('');
  const [procedureGroup, setProcedureGroup] = useState('');
  const [drgCode, setDrgCode] = useState('');
  const [tariff, setTariff] = useState('');
  const [topUp, setTopUp] = useState('');
  const [specialty, setSpecialty] = useState('');
  const [specialtyCode, setSpecialtyCode] = useState('');
  const [selectedInsurances, setSelectedInsurances] = useState({});
  const [insuranceSearch, setInsuranceSearch] = useState('');
  const [errors, setErrors] = useState({});

  const selectedInsuranceIds = Object.keys(selectedInsurances).filter(id => selectedInsurances[id]);
  const selectedCount = selectedInsuranceIds.length;

  const clearError = (field) => setErrors(prev => { const n = { ...prev }; delete n[field]; return n; });

  const handleInsuranceToggle = (id) => {
    setSelectedInsurances(prev => ({ ...prev, [id]: !prev[id] }));
    clearError('insurance');
  };
  const handleSelectAll = () => {
    const all = {};
    insuranceProviders.forEach(i => { all[i.id] = true; });
    setSelectedInsurances(all);
    clearError('insurance');
  };
  const handleDeselectAll = () => setSelectedInsurances({});

  /* ── validation ──────────────────────────────────────────── */
  const validate = () => {
    const e = {};

    if (!ageCategory)
      e.ageCategory = 'Age Category is required';

    if (!procedureName.trim())
      e.procedureName = 'Procedure Name is required';

    if (!procedureGroup.trim())
      e.procedureGroup = 'Procedure Group is required';

    if (!drgCode.trim())
      e.drgCode = 'DRG Code is required';

    // Tariff: required, must be a number ≥ 0
    if (tariff === '' || tariff === null || tariff === undefined)
      e.tariff = 'Tariff is required';
    else if (isNaN(parseFloat(tariff)) || parseFloat(tariff) < 0)
      e.tariff = 'Must be a valid number (0 or more)';

    // Top-up: required, must be a number ≥ 0
    if (topUp === '' || topUp === null || topUp === undefined)
      e.topUp = 'Top-up is required';
    else if (isNaN(parseFloat(topUp)) || parseFloat(topUp) < 0)
      e.topUp = 'Must be a valid number (0 or more)';

    if (!specialty.trim())
      e.specialty = 'Specialty is required';

    if (!specialtyCode.trim())
      e.specialtyCode = 'Specialty Code is required';

    if (selectedCount === 0)
      e.insurance = 'At least one insurance provider must be selected';

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  /* ── save handler ────────────────────────────────────────── */
  const handleSave = async () => {
    if (!validate()) {
      const pending = {
        ...(!ageCategory ? { a: 'Age Category must be selected' } : {}),
        ...(!procedureName.trim() ? { b: 'Procedure Name must be entered' } : {}),
        ...(!procedureGroup.trim() ? { c: 'Procedure Group must be entered' } : {}),
        ...(!drgCode.trim() ? { d: 'DRG Code must be entered' } : {}),
        ...(tariff === '' ? { f: 'Tariff must be entered' } : {}),
        ...(topUp === '' ? { g: 'Top-up must be entered' } : {}),
        ...(!specialty.trim() ? { h: 'Specialty must be entered' } : {}),
        ...(!specialtyCode.trim() ? { i: 'Specialty Code must be entered' } : {}),
        ...(selectedCount === 0 ? { j: 'At least one insurance must be selected' } : {}),
      };
      await Swal.fire({
        title: 'Please complete all required fields',
        html: `<ul style="text-align:left;margin:0;padding-left:1.2em;">${Object.values(pending).map(m => `<li>${m}</li>`).join('')}</ul>`,
        icon: 'warning',
        confirmButtonColor: '#217A3C',
      });
      return;
    }

    setSaving(true);
    try {
      const rows = selectedInsuranceIds.map(id => {
        const provider = insuranceProviders.find(p => String(p.id) === String(id));
        return {
          age: ageCategory,
          procedure_: procedureName.trim(),
          procedure_group: procedureGroup.trim(),
          G_DRG: drgCode.trim(),
          tariff: parseFloat(tariff),
          topup: parseFloat(topUp),
          spe: specialty.trim(),
          spe_code: specialtyCode.trim(),
          status: provider?.name || ''
        };
      });

      const response = await axios.post(saveNewProcedure, { rows });

      if (response.data.success) {
        await Swal.fire({
          title: 'Saved!',
          html: `<b>${procedureName}</b> saved with <b>${selectedCount}</b> insurance record${selectedCount !== 1 ? 's' : ''}.`,
          icon: 'success',
          timer: 2500,
          showConfirmButton: false,
          timerProgressBar: true,
        });
        if (onSaved) onSaved();
        handleClose();
      } else {
        throw new Error(response.data.message || 'Save failed');
      }
    } catch (err) {
      await Swal.fire({
        title: 'Save Failed',
        text: err.response?.data?.message || err.message || 'An error occurred while saving',
        icon: 'error',
        confirmButtonColor: '#D64545',
      });
    } finally {
      setSaving(false);
    }
  };

  /* ── reset & close ───────────────────────────────────────── */
  const handleClose = () => {
    setAgeCategory(''); setProcedureName(''); setProcedureGroup('');
    setDrgCode(''); setTariff(''); setTopUp('');
    setSpecialty(''); setSpecialtyCode('');
    setSelectedInsurances({}); setInsuranceSearch('');
    setErrors({});
    onClose();
  };

  const filteredProviders = insuranceProviders.filter(i =>
    i.name?.toLowerCase().includes(insuranceSearch.toLowerCase())
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth TransitionComponent={Fade}
      PaperProps={{ sx: {
        borderRadius: { xs: 2, sm: 3 }, overflow: 'hidden', boxShadow: '0 24px 64px rgba(0,0,0,0.18)',
        //  On a phone the dialog takes the screen rather than sitting in a tiny
        //  box inside it — the default 32px margin leaves a two-column form
        //  competing for about 280px.
        m: { xs: 1, sm: 4 },
        width: { xs: 'calc(100% - 16px)', sm: 'calc(100% - 64px)' },
        maxHeight: { xs: 'calc(100% - 16px)', sm: 'calc(100% - 64px)' },
      } }}>

      <DialogTitle sx={{ background: 'linear-gradient(135deg,#123A6D,#0B2452)', color: 'white', px: { xs: 2, sm: 2.5 }, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75, minWidth: 0 }}>
          <Box sx={{
            width: 36, height: 36, borderRadius: '10px', flexShrink: 0,
            background: 'linear-gradient(135deg,#3FBF64,#217A3C)',
            display: 'grid', placeItems: 'center',
          }}>
            <ProcedureIcon sx={{ fontSize: 19 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 16, fontWeight: 800, lineHeight: 1.25 }}>New Procedure</Typography>
            <Typography noWrap sx={{ fontSize: 12, opacity: .8 }}>Configure procedure details and assign insurance providers</Typography>
          </Box>
        </Box>
        <IconButton onClick={handleClose} size="small" aria-label="Close" sx={{ color: 'white', flexShrink: 0 }}><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {/* CSS Grid, not <Grid>. The MUI 7 Grid here took `item xs={12} md={6}`,
            which that version ignores, and a bare `size={6}` — a fixed half-width
            at EVERY breakpoint. So the two panels stayed 50/50 on a phone. This
            gives one column up to md and two beyond it. */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1fr) minmax(0, 1fr)' },
          minHeight: { md: 520 },
        }}>

          {/* ── LEFT: procedure fields ───────────────────────── */}
          <Box sx={{
            p: { xs: 2, sm: 3 }, minWidth: 0, background: '#F4F7FB',
            borderRight: { md: '1px solid #DDE5EF' },
            borderBottom: { xs: '1px solid #DDE5EF', md: 'none' },
          }}>
            <PxSectionLabel error={!!errors.ageCategory}>Procedure Details</PxSectionLabel>

            {/* Age Category */}
            <FormControl component="fieldset" sx={{ mb: 1.5, width: '100%' }}>
              <Typography sx={{ fontWeight: 700, fontSize: 11, letterSpacing: '.5px', textTransform: 'uppercase', color: errors.ageCategory ? '#D64545' : '#93A2B8', mb: .75, display: 'block' }}>
                Age Category *
              </Typography>
              <RadioGroup row value={ageCategory} sx={{ gap: .75 }}
                onChange={e => { setAgeCategory(e.target.value); clearError('ageCategory'); }}>
                {['Adult', 'Child', 'Both'].map(v => (
                  <FormControlLabel key={v} value={v} sx={pxChoiceSx(ageCategory === v)}
                    control={<Radio size="small" sx={{ p: .25, mr: .5, color: '#93A2B8', '&.Mui-checked': { color: '#fff' } }} />}
                    label={<Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>{v}</Typography>} />
                ))}
              </RadioGroup>
              {errors.ageCategory && <Typography sx={{ color: '#D64545', fontSize: 11, mt: .5 }}>{errors.ageCategory}</Typography>}
            </FormControl>

            <Divider sx={{ my: 1.5, borderColor: '#DDE5EF' }} />

            {/* Six fields stacked single-file made this dialog scroll. The short
                ones pair up now — a code beside a code, a tariff beside a top-up —
                and only the two long names take a full row. */}
            <Box sx={{
              display: 'grid', gap: 1.5,
              gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' },
            }}>
              <Box sx={{ gridColumn: { sm: '1 / -1' } }}>
                <TextField fullWidth size="small" label="Procedure Name *" value={procedureName}
                  onChange={e => { setProcedureName(e.target.value); clearError('procedureName'); }}
                  error={!!errors.procedureName} helperText={errors.procedureName || ' '}
                  placeholder="e.g., Temporary Filling, Tooth Extraction" sx={adminFieldSx} />
              </Box>

              <Box sx={{ gridColumn: { sm: '1 / -1' } }}>
                <TextField fullWidth size="small" label="Procedure Group *" value={procedureGroup}
                  onChange={e => { setProcedureGroup(e.target.value); clearError('procedureGroup'); }}
                  error={!!errors.procedureGroup} helperText={errors.procedureGroup || ' '}
                  placeholder="e.g., MDC: DENTAL, MDC: SURGERY" sx={adminFieldSx} />
              </Box>

              <TextField fullWidth size="small" label="DRG Code *" value={drgCode}
                onChange={e => { setDrgCode(e.target.value); clearError('drgCode'); }}
                error={!!errors.drgCode} helperText={errors.drgCode || ' '}
                placeholder="e.g., DENT03" sx={adminFieldSx} />

              <TextField fullWidth size="small" label="Specialty Code *" value={specialtyCode}
                onChange={e => { setSpecialtyCode(e.target.value); clearError('specialtyCode'); }}
                error={!!errors.specialtyCode} helperText={errors.specialtyCode || ' '}
                placeholder="e.g., DENT" sx={adminFieldSx} />

              <TextField fullWidth size="small" label="Tariff GH¢ *" type="number" value={tariff}
                onChange={e => { setTariff(e.target.value); clearError('tariff'); }}
                error={!!errors.tariff} helperText={errors.tariff || ' '}
                inputProps={{ min: 0, step: '0.01' }} sx={adminFieldSx} />

              <TextField fullWidth size="small" label="Top-up GH¢ *" type="number" value={topUp}
                onChange={e => { setTopUp(e.target.value); clearError('topUp'); }}
                error={!!errors.topUp} helperText={errors.topUp || ' '}
                inputProps={{ min: 0, step: '0.01' }} sx={adminFieldSx} />

              <Box sx={{ gridColumn: { sm: '1 / -1' } }}>
                <TextField fullWidth size="small" label="Specialty *" value={specialty}
                  onChange={e => { setSpecialty(e.target.value); clearError('specialty'); }}
                  error={!!errors.specialty} helperText={errors.specialty || ' '}
                  placeholder="e.g., DENTAL, SURGERY, PAEDIATRICS" sx={adminFieldSx} />
              </Box>
            </Box>
          </Box>

          {/* ── RIGHT: insurance checkboxes ────────────────── */}
          <Box sx={{ p: { xs: 2, sm: 3 }, minWidth: 0, display: 'flex', flexDirection: 'column', background: '#fff' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
              <Box>
                <PxSectionLabel error={!!errors.insurance}>Insurance Providers *</PxSectionLabel>
                <Typography variant="caption" color={errors.insurance ? 'error' : 'text.secondary'}>
                  {selectedCount} of {insuranceProviders.length} selected
                </Typography>
              </Box>
              {selectedCount > 0 && (
                <Chip size="small" icon={<CheckCircleIcon sx={{ fontSize: '14px !important' }} />}
                  label={`${selectedCount} selected`}
                  sx={{ background: '#E7F5EC', color: '#217A3C', fontWeight: 600, fontSize: '0.7rem' }} />
              )}
            </Box>

            {errors.insurance && (
              <Typography variant="caption" sx={{ color: '#D64545', fontSize: '0.7rem', mb: 0.5 }}>
                {errors.insurance}
              </Typography>
            )}

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5, mt: 0.5 }}>
              <Button size="small" variant="outlined" onClick={handleSelectAll}
                sx={{ borderColor: '#123A6D', color: '#123A6D', fontSize: '0.72rem', py: 0.4 }}>
                Select All
              </Button>
              <Button size="small" variant="outlined" onClick={handleDeselectAll}
                sx={{ borderColor: '#123A6D', color: '#123A6D', fontSize: '0.72rem', py: 0.4 }}>
                Deselect All
              </Button>
            </Box>

            <TextField fullWidth size="small" placeholder="Search insurance..."
              value={insuranceSearch} onChange={e => setInsuranceSearch(e.target.value)}
              sx={{ ...adminFieldSx, mb: 1.5 }}
              InputProps={{ endAdornment: (<InputAdornment position="end"><SearchIcon sx={{ fontSize: 18, color: '#93A2B8' }} /></InputAdornment>) }} />

            <Box sx={{
              flex: 1, overflowY: 'auto',
              border: `1px solid ${errors.insurance ? '#D64545' : '#F4F7FB'}`,
              borderRadius: 2, p: 1, minHeight: 280, maxHeight: 320,
              transition: 'border-color 0.2s',
              '&::-webkit-scrollbar': { width: '6px' },
              '&::-webkit-scrollbar-thumb': { background: '#DDE5EF', borderRadius: '10px' },
            }}>
              {loadingInsurance ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <CircularProgress size={28} />
                </Box>
              ) : filteredProviders.length === 0 ? (
                <EmptyState
                    icon={InsuranceIcon}
                    title="No providers"
                    body="No insurance scheme matched."
                  />
              ) : (
                filteredProviders.map(insurance => (
                  <Box key={insurance.id} onClick={() => handleInsuranceToggle(insurance.id)}
                    sx={{
                      display: 'flex', alignItems: 'center', p: '4px 8px', borderRadius: 1,
                      cursor: 'pointer', mb: 0.5,
                      background: selectedInsurances[insurance.id] ? 'rgba(18,58,109,0.06)' : 'transparent',
                      border: selectedInsurances[insurance.id] ? '1px solid rgba(18,58,109,0.22)' : '1px solid transparent',
                      transition: 'all 0.15s ease',
                      '&:hover': { background: 'rgba(18,58,109,0.05)' },
                    }}>
                    <Checkbox size="small"
                      checked={selectedInsurances[insurance.id] || false}
                      onChange={() => handleInsuranceToggle(insurance.id)}
                      onClick={e => e.stopPropagation()}
                      sx={{ p: 0.5, color: '#2E9E4F', '&.Mui-checked': { color: '#2E9E4F' } }} />
                    <Typography variant="body2" sx={{ ml: 0.5, fontSize: '0.82rem', color: '#0F1F3D' }}>
                      {insurance.name}
                    </Typography>
                  </Box>
                ))
              )}
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: '1px solid #DDE5EF', background: '#F4F7FB', gap: 1 }}>
        <Button onClick={handleClose} startIcon={<ClearIcon />} variant="outlined" disabled={saving}
          sx={brandGhostButtonSx}>
          Cancel
        </Button>
        <Button variant="contained"
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          onClick={handleSave} disabled={saving}
          sx={{ ...brandButtonSx, px: 3, '&:disabled': { background: '#DDE5EF', color: '#93A2B8' } }}>
          {saving ? 'Saving...' : 'Save Procedure'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/* ─────────────────────────────────────────────────────────────
   Main Component
───────────────────────────────────────────────────────────── */
const Procedure = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [insuranceProviders, setInsuranceProviders] = useState([]);
  const [procedures, setProcedures] = useState([]);
  const [loadingInsurance, setLoadingInsurance] = useState(false);
  const [loadingProcedures, setLoadingProcedures] = useState(false);
  const [loadingProcedureInsurances, setLoadingProcedureInsurances] = useState(false);
  const [updatingRowId, setUpdatingRowId] = useState(null);
  const [deletingRowId, setDeletingRowId] = useState(null);
  const [error, setError] = useState(null);
  const [selectedProcedure, setSelectedProcedure] = useState(null);
  const [procedureInsuranceRows, setProcedureInsuranceRows] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const [bulkApplyDialogOpen, setBulkApplyDialogOpen] = useState(false);
  const [sourceRowForBulkApply, setSourceRowForBulkApply] = useState(null);
  const [bulkValues, setBulkValues] = useState(EMPTY_BULK);
  const [bulkApplying, setBulkApplying] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const loadInsuranceProviders = async () => {
    setLoadingInsurance(true); setError(null);
    try {
      const res = await axios.get(loadInsurance);
      if (res.data.success) setInsuranceProviders(res.data.results);
      else setError('Failed to load insurance providers');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load insurance providers');
      await Swal.fire({ title: 'Error', text: err.response?.data?.message || 'Failed to load insurance providers', icon: 'error' });
    } finally { setLoadingInsurance(false); }
  };

  const loadProceduresFromBackend = async () => {
    setLoadingProcedures(true); setError(null);
    try {
      const res = await axios.get(loadProcedures);
      if (res.data.success) setProcedures(res.data.results);
      else setError('Failed to load procedures');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load procedures');
      await Swal.fire({ title: 'Error', text: err.response?.data?.message || 'Failed to load procedures', icon: 'error' });
    } finally { setLoadingProcedures(false); }
  };

  const loadProcedureInsurancesForDisplay = async (procedureId, procedureName) => {
    setLoadingProcedureInsurances(true); setProcedureInsuranceRows([]); setSelectedRowIds([]);
    try {
      const res = await axios.post(getProcedureInsurances, { procedureId, procedureName });
      if (res.data.success) setProcedureInsuranceRows(res.data.results);
    } catch (err) {
      await Swal.fire({ title: 'Error', text: err.response?.data?.message || 'Failed to load procedure insurances', icon: 'error' });
    } finally { setLoadingProcedureInsurances(false); }
  };

  const handleUpdateRow = async (row) => {
    if (!row.tariff || !row.topup || !row.age) {
      await Swal.fire({ title: 'Validation Error', text: 'Please fill in all required fields (Tariff, Top-up, Age Category)', icon: 'warning' }); return;
    }
    if (!row.id) { await Swal.fire({ title: 'Error', text: 'Record ID is missing. Cannot update.', icon: 'error' }); return; }
    setUpdatingRowId(row.id);
    try {
      const res = await axios.post(updateInsuredProcedure, {
        id: row.id,
        tariff: parseFloat(row.tariff),
        topup: parseFloat(row.topup),
        age: row.age,
        G_DRG: row.G_DRG || '',
        spe: row.spe || '',
        spe_code: row.spe_code || '',
        // Always sent, including when blank, so clearing the field on screen
        // clears it in the table. The endpoint treats an ABSENT icd as "leave it"
        // — which is right for callers that have no field, and wrong here.
        icd: row.icd ?? ''
      });
      if (res.data.success) {
        await Swal.fire({ title: 'Success!', text: `Successfully updated ${row.status}`, icon: 'success', timer: 2000, showConfirmButton: false });
        if (selectedProcedure) await loadProcedureInsurancesForDisplay(selectedProcedure.id, selectedProcedure.procedure_);
      } else throw new Error(res.data.message || 'Update failed');
    } catch (err) {
      await Swal.fire({ title: 'Update Failed', text: err.response?.data?.message || err.message || 'Failed to update', icon: 'error' });
    } finally { setUpdatingRowId(null); }
  };

  const handleDeleteRow = async (row) => {
    if (!row.id) { await Swal.fire({ title: 'Error', text: 'Record ID is missing. Cannot delete.', icon: 'error' }); return; }
    const result = await Swal.fire({
      title: 'Confirm Delete',
      html: `Are you sure you want to delete the record for <strong>${row.status || 'this entry'}</strong>?<br/><span style="color:#D64545;font-size:0.9rem;">This action cannot be undone.</span>`,
      icon: 'warning', showCancelButton: true, confirmButtonColor: '#D64545', cancelButtonColor: '#93A2B8',
      confirmButtonText: 'Yes, Delete', cancelButtonText: 'Cancel',
    });
    if (!result.isConfirmed) return;
    setDeletingRowId(row.id);
    try {
      const res = await axios.post(deleteInsuredProcedure, { id: row.id });
      if (res.data.success) {
        await Swal.fire({ title: 'Deleted!', text: `Record for "${row.status}" has been deleted.`, icon: 'success', timer: 2000, showConfirmButton: false });
        setProcedureInsuranceRows(prev => prev.filter(r => r.id !== row.id));
        setSelectedRowIds(prev => prev.filter(id => id !== row.id));
      } else throw new Error(res.data.message || 'Delete failed');
    } catch (err) {
      await Swal.fire({ title: 'Delete Failed', text: err.response?.data?.message || err.message || 'Failed to delete', icon: 'error' });
    } finally { setDeletingRowId(null); }
  };

  const handleRowSelectionToggle = (rowId) => {
    setSelectedRowIds(prev => 
      prev.includes(rowId) ? prev.filter(id => id !== rowId) : [...prev, rowId]
    );
  };

  const handleSelectAllRows = () => {
    if (selectedRowIds.length === procedureInsuranceRows.length) {
      setSelectedRowIds([]);
    } else {
      setSelectedRowIds(procedureInsuranceRows.map(r => r.id));
    }
  };

  const handleBulkApply = () => {
    if (selectedRowIds.length !== 1) {
      Swal.fire({
        title: 'Selection Required',
        text: 'Please select exactly ONE row to use as template for bulk insurance assignment.',
        icon: 'info',
        confirmButtonColor: '#217A3C',
      });
      return;
    }

    const sourceRow = procedureInsuranceRows.find(r => r.id === selectedRowIds[0]);
    if (!sourceRow) return;

    setSourceRowForBulkApply(sourceRow);
    setBulkApplyDialogOpen(true);
  };

  /* ─────────────────────────────────────────────────────────────
     Bulk edit bar — apply typed values / delete, across ticked rows
     ───────────────────────────────────────────────────────────── */
  //
  //  Loops the existing single-row endpoints rather than a new bulk SQL path —
  //  same reasoning as Diagnosis Setup: those endpoints are already validated
  //  and in use, and one procedure has only a handful of schemes.
  //
  //  A procedure has no ICD-10 (that belongs to a diagnosis), so the bar carries
  //  three fields rather than four.

  const bulkTargets = () => procedureInsuranceRows.filter(r => selectedRowIds.includes(r.id));

  const handleBarApply = async () => {
    const targets = bulkTargets();
    if (targets.length === 0) return;

    const patch = {};
    if (String(bulkValues.tariff).trim() !== '') patch.tariff = bulkValues.tariff.trim();
    if (String(bulkValues.topup).trim()  !== '') patch.topup  = bulkValues.topup.trim();
    if (String(bulkValues.G_DRG).trim()  !== '') patch.G_DRG  = bulkValues.G_DRG.trim();
    if (String(bulkValues.icd).trim()    !== '') patch.icd    = bulkValues.icd.trim();
    if (Object.keys(patch).length === 0) return;

    const confirm = await Swal.fire({
      title: `Apply to ${targets.length} scheme${targets.length === 1 ? '' : 's'}?`,
      html: `<div style="text-align:left;font-size:13px">
               ${Object.entries(patch).map(([k, v]) => `<div><b>${k}</b> → ${v}</div>`).join('')}
               <div style="margin-top:8px;color:#5A6B87">Fields left blank keep their current values.</div>
             </div>`,
      icon: 'question', showCancelButton: true,
      confirmButtonColor: '#123A6D', cancelButtonColor: '#5A6B87',
      confirmButtonText: 'Yes, apply',
    });
    if (!confirm.isConfirmed) return;

    setBulkApplying(true);
    let ok = 0; const failed = [];
    for (const row of targets) {
      try {
        // Current values sent for anything the patch does not cover — the
        // endpoint writes every column it receives.
        const res = await axios.post(updateInsuredProcedure, {
          id:       row.id,
          tariff:   patch.tariff ?? row.tariff,
          topup:    patch.topup  ?? row.topup,
          age:      row.age,
          G_DRG:    patch.G_DRG  ?? row.G_DRG,
          spe:      row.spe,
          spe_code: row.spe_code,
          icd:      patch.icd    ?? (row.icd ?? ''),
        });
        if (res.data.success) ok++; else failed.push(row.status || row.id);
      } catch (_) { failed.push(row.status || row.id); }
    }
    setBulkApplying(false);

    if (selectedProcedure) {
      await loadProcedureInsurancesForDisplay(selectedProcedure.id, selectedProcedure.name);
    }
    setBulkValues(EMPTY_BULK);

    await Swal.fire({
      title: failed.length === 0 ? 'Applied' : 'Partly applied',
      text: failed.length === 0
        ? `${ok} scheme${ok === 1 ? '' : 's'} updated.`
        : `${ok} updated. Failed: ${failed.join(', ')}.`,
      icon: failed.length === 0 ? 'success' : 'warning',
      timer: failed.length === 0 ? 2200 : undefined,
      showConfirmButton: failed.length > 0,
    });
  };

  const handleBarDelete = async () => {
    const targets = bulkTargets();
    if (targets.length === 0) return;

    const confirm = await Swal.fire({
      title: `Delete ${targets.length} record${targets.length === 1 ? '' : 's'}?`,
      html: `<div style="text-align:left;font-size:13px">
               ${targets.map(r => `<div>• ${r.status || r.id}</div>`).join('')}
               <div style="margin-top:8px;color:#B23A3A">This cannot be undone.</div>
             </div>`,
      icon: 'warning', showCancelButton: true,
      confirmButtonColor: '#D64545', cancelButtonColor: '#5A6B87',
      confirmButtonText: `Yes, delete ${targets.length}`,
    });
    if (!confirm.isConfirmed) return;

    setBulkDeleting(true);
    let ok = 0; const failed = [];
    for (const row of targets) {
      try {
        const res = await axios.post(deleteInsuredProcedure, { id: row.id });
        if (res.data.success) ok++; else failed.push(row.status || row.id);
      } catch (_) { failed.push(row.status || row.id); }
    }
    setBulkDeleting(false);
    setSelectedRowIds([]);

    if (selectedProcedure) {
      await loadProcedureInsurancesForDisplay(selectedProcedure.id, selectedProcedure.name);
    }

    await Swal.fire({
      title: failed.length === 0 ? 'Deleted' : 'Partly deleted',
      text: failed.length === 0
        ? `${ok} record${ok === 1 ? '' : 's'} removed.`
        : `${ok} removed. Failed: ${failed.join(', ')}.`,
      icon: failed.length === 0 ? 'success' : 'warning',
      timer: failed.length === 0 ? 2200 : undefined,
      showConfirmButton: failed.length > 0,
    });
  };

  useEffect(() => { loadInsuranceProviders(); loadProceduresFromBackend(); }, []);

  // ── Rename the procedure itself ───────────────────────────────────────────
  //  The mirror of the diagnosis rename — same reasoning, same guardrails. See
  //  handleRename in Diagnosis.js and renameCatalogue in server.js.
  //
  //  The column is `procedure_tarf.procedure_`, not `name`; the server knows
  //  which, so this sends only the old and new wording.
  const handleRename = async () => {
    if (!selectedProcedure) return;
    const current = selectedProcedure.procedure_ || '';
    const { value, isConfirmed } = await Swal.fire({
      title: 'Rename this procedure',
      input: 'text',
      inputValue: current,
      inputLabel: `Every insurance record filed under "${current}" is renamed with it.`,
      inputAttributes: { maxlength: 190, autocapitalize: 'off' },
      showCancelButton: true,
      confirmButtonText: 'Rename',
      inputValidator: (v) => {
        const s = String(v || '').trim();
        if (!s) return 'The name cannot be empty';
        if (s === current) return 'That is already its name';
        return null;
      },
    });
    if (!isConfirmed) return;

    try {
      const { data } = await axios.post(renameProcedure, { from: current, to: String(value).trim() });
      if (!data.success) throw new Error(data.message || 'The rename did not go through');
      setSelectedProcedure({ ...selectedProcedure, procedure_: data.to });
      await loadProceduresFromBackend();
      await loadProcedureInsurancesForDisplay(selectedProcedure.id, data.to);
      await Swal.fire({
        title: 'Renamed', icon: 'success',
        text: `${data.changed} record${data.changed === 1 ? '' : 's'} now read "${data.to}". `
            + 'The claims screens show the new name as their catalogue refreshes.',
      });
    } catch (err) {
      await Swal.fire({
        title: 'Not renamed', icon: 'error',
        text: err.response?.data?.message || err.message,
      });
    }
  };

  // ── Will a claim actually offer this procedure? ───────────────────────────
  //  The mirror of checkClaimsSide in Diagnosis.js — same reasoning, same
  //  endpoint, `kind: 'procedure'`. See that file's note.
  const [checkingVisibility, setCheckingVisibility] = useState(false);
  const checkClaimsSide = async () => {
    if (!selectedProcedure) return;
    setCheckingVisibility(true);
    try {
      const { data } = await axios.get(catalogueVisibility, {
        params: { kind: 'procedure', name: selectedProcedure.procedure_ },
      });
      if (!data.success) throw new Error(data.message || 'The check did not run');
      const rows = data.rows || [];
      if (!rows.length) {
        await Swal.fire({ title: 'Nothing to check', icon: 'info',
          text: data.note || 'No insurance records are filed under this name.' });
        return;
      }
      const cell = (r) => {
        const where = r.status || 'no scheme';
        if (r.offered) {
          return `<tr><td style="padding:4px 8px">✅</td>`
               + `<td style="padding:4px 8px"><b>${where}</b></td>`
               + `<td style="padding:4px 8px">offered to ${r.offered_to.join(' and ')}</td></tr>`;
        }
        return `<tr><td style="padding:4px 8px;vertical-align:top">❌</td>`
             + `<td style="padding:4px 8px;vertical-align:top"><b>${where}</b></td>`
             + `<td style="padding:4px 8px;color:#8A5510">${r.why || 'Not offered.'}</td></tr>`;
      };
      const hidden = rows.filter((r) => !r.offered).length;
      await Swal.fire({
        title: hidden ? 'Not everything is usable' : 'Usable on claims',
        icon: hidden ? 'warning' : 'success',
        width: 720,
        html: `<div style="text-align:left;font-size:13px">`
            + `<p style="margin:0 0 8px">Checked by running the claims picker itself, `
            + `for an adult and for a child.</p>`
            + `<table style="width:100%;border-collapse:collapse">${rows.map(cell).join('')}</table>`
            + (hidden
              ? `<p style="margin:10px 0 0;color:#8A5510">A record marked ❌ can never be `
                + `picked on a claim until the reason beside it is fixed.</p>`
              : `<p style="margin:10px 0 0;color:#217A3C">Every record is offered. If it is `
                + `still not in the list on a claim already open, reload that page — the `
                + `catalogue is held for five minutes.</p>`)
            + `</div>`,
      });
    } catch (err) {
      await Swal.fire({ title: 'Could not check', icon: 'error',
        text: err.response?.data?.message || err.message });
    } finally {
      setCheckingVisibility(false);
    }
  };

  const handleProcedureClick = async (procedure) => {
    if (selectedProcedure?.id === procedure.id) { 
      setSelectedProcedure(null); 
      setProcedureInsuranceRows([]); 
      setSelectedRowIds([]);
      return; 
    }
    setSelectedProcedure(procedure);
    await loadProcedureInsurancesForDisplay(procedure.id, procedure.procedure_);
  };

  const handleRowChange = (idx, field, value) =>
    setProcedureInsuranceRows(prev => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));

  const existingStatuses = procedureInsuranceRows.map(r => r.status);

  return (
    <>
      {/* One navy page header, replacing the teal→lime band and its three
          competing "contained" buttons. Same treatment as Diagnosis Setup. */}
      <AdminPageHead
        icon={ProcedureIcon}
        title="Procedure Setup"
        subtitle="Procedure tariffs and top-ups, per insurance scheme"
        actions={
          <>
            <Tooltip title="Reload procedures" arrow>
              <span>
                <IconButton
                  onClick={loadProceduresFromBackend} disabled={loadingProcedures} size="small"
                  aria-label="Reload procedures"
                  sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,.14)', '&:hover': { bgcolor: 'rgba(255,255,255,.24)' } }}
                >
                  {loadingProcedures ? <CircularProgress size={15} sx={{ color: '#fff' }} /> : <RefreshIcon sx={{ fontSize: 18 }} />}
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Reload insurance schemes" arrow>
              <span>
                <IconButton
                  onClick={loadInsuranceProviders} disabled={loadingInsurance} size="small"
                  aria-label="Reload insurance schemes"
                  sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,.14)', '&:hover': { bgcolor: 'rgba(255,255,255,.24)' } }}
                >
                  {loadingInsurance ? <CircularProgress size={15} sx={{ color: '#fff' }} /> : <CloudDownload sx={{ fontSize: 18 }} />}
                </IconButton>
              </span>
            </Tooltip>
            <Button
              onClick={() => setDialogOpen(true)} size="small" startIcon={<AddIcon />}
              sx={brandButtonSx}
            >
              New Procedure
            </Button>
          </>
        }
      />

      <Paper elevation={0} sx={{
        borderRadius: ADMIN.radius, boxShadow: ADMIN.shadow, background: '#fff',
        border: '1px solid #DDE5EF', overflow: 'hidden',
      }}>
        <Box sx={{ p: 3 }}>
          {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}
          {/* One third / two thirds on desktop, stacked below it. The MUI grid
              this replaces used `size={4}` and `size={8}` — fixed ratios at every
              breakpoint, so a phone got a 33% column holding procedure names. */}
          <Box sx={{
            display: 'grid', gap: 3, alignItems: 'start',
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1fr) minmax(0, 2fr)' },
          }}>

            {/* Left: procedure list */}
            <Box sx={{ minWidth: 0 }}>
              <Paper elevation={0} sx={{ p: 2.5, border: '1px solid #DDE5EF', borderRadius: ADMIN.radiusSm, background: '#fff', height: { xs: 420, md: 580 }, display: 'flex', flexDirection: 'column' }}>
                <TextField fullWidth size="small" placeholder="Search procedures..." value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)} sx={{ ...adminFieldSx, mb: 2 }}
                  InputProps={{ endAdornment: (<InputAdornment position="end"><SearchIcon sx={{ fontSize: 18, color: '#2E9E4F' }} /></InputAdornment>) }} />
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0F1F3D' }}>Available Procedures</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {loadingProcedures && <CircularProgress size={16} />}
                    <Chip size="small" label={`${procedures.length} total`} sx={{ fontSize: '0.68rem', background: '#F4F7FB', color: '#123A6D' }} />
                  </Box>
                </Box>
                {selectedProcedure && (
                  <Box sx={{ mb: 1.5, p: 1, borderRadius: 1.5, background: 'linear-gradient(90deg,rgba(18,58,109,0.08),rgba(46,158,79,0.07))', border: '1px solid rgba(18,58,109,0.22)', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircleIcon sx={{ fontSize: 16, color: '#217A3C' }} />
                    <Typography variant="caption" sx={{ color: '#123A6D', fontWeight: 600 }}>Selected: {selectedProcedure.procedure_}</Typography>
                    <IconButton size="small" onClick={() => { setSelectedProcedure(null); setProcedureInsuranceRows([]); setSelectedRowIds([]); }} sx={{ ml: 'auto', p: 0.2 }}>
                      <CloseIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Box>
                )}
                <Box sx={{ flex: 1, overflowY: 'auto', pr: 0.5, '&::-webkit-scrollbar': { width: '6px' }, '&::-webkit-scrollbar-thumb': { background: '#DDE5EF', borderRadius: '10px' } }}>
                  {loadingProcedures ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}><CircularProgress /></Box>
                  ) : procedures.length === 0 ? (
                    <EmptyState
                      icon={ProcedureIcon}
                      title="No procedures"
                      body="Nothing loaded yet — use Reload Procedures in the header."
                    />
                  ) : (
                    procedures.filter(p => p.procedure_?.toLowerCase().includes(searchTerm.toLowerCase())).map(procedure => (
                      <Box key={procedure.id} onClick={() => handleProcedureClick(procedure)}
                        sx={{ display: 'flex', alignItems: 'center', mb: 0.75, p: 1.25, borderRadius: 1.5, cursor: 'pointer', transition: 'all 0.18s ease', border: selectedProcedure?.id === procedure.id ? '1.5px solid #2E9E4F' : '1.5px solid transparent', background: selectedProcedure?.id === procedure.id ? 'rgba(18,58,109,0.06)' : 'transparent', '&:hover': { background: 'rgba(18,58,109,0.05)', transform: 'translateX(3px)', borderColor: 'rgba(18,58,109,0.3)' } }}>
                        <ProcedureIcon sx={{ fontSize: 16, mr: 1, color: selectedProcedure?.id === procedure.id ? '#2E9E4F' : '#93A2B8', transition: 'color 0.18s' }} />
                        <Typography variant="body2" sx={{ fontSize: '0.85rem', color: selectedProcedure?.id === procedure.id ? '#123A6D' : '#0F1F3D', fontWeight: selectedProcedure?.id === procedure.id ? 700 : 400 }}>
                          {procedure.procedure_}
                        </Typography>
                      </Box>
                    ))
                  )}
                </Box>
              </Paper>
            </Box>

            {/* Right: editable records table */}
            <Box sx={{ minWidth: 0 }}>
              <Paper elevation={0} sx={{ border: '1px solid #DDE5EF', borderRadius: ADMIN.radiusSm, background: '#fff', height: 580, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <Box sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid #F4F7FB', background: '#F4F7FB', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0F1F3D' }}>Insurance Coverage Records</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        {selectedProcedure ? `Records for: ${selectedProcedure.procedure_}` : 'Select a procedure to load records'}
                      </Typography>
                      {/*  Edit the name itself — see the diagnosis screen. It
                           renames every insurance record under this procedure,
                           because the list on the left is by name. */}
                      {selectedProcedure && (
                        <Tooltip title="Rename this procedure — every insurance record under it">
                          <IconButton
                            size="small"
                            onClick={handleRename}
                            aria-label={`Rename ${selectedProcedure.procedure_}`}
                            sx={{ p: 0.25, color: ADMIN.brand || '#1976d2' }}>
                            <EditIcon sx={{ fontSize: 15 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                      {/*  Whether a claim can actually offer it — see the note on
                           checkClaimsSide, and the diagnosis screen. */}
                      {selectedProcedure && (
                        <Tooltip title="Check whether the claims side can offer this procedure">
                          <IconButton
                            size="small"
                            onClick={checkClaimsSide}
                            disabled={checkingVisibility}
                            aria-label={`Check the claims side for ${selectedProcedure.procedure_}`}
                            sx={{ p: 0.25, color: '#217A3C' }}>
                            <VisibilityIcon sx={{ fontSize: 15 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    {selectedRowIds.length > 0 && (
                      <Chip 
                        size="small" 
                        label={`${selectedRowIds.length} selected`}
                        sx={{ background: '#e3f2fd', color: '#1976d2', fontWeight: 600, fontSize: '0.7rem' }}
                      />
                    )}
                    {selectedProcedure && !loadingProcedureInsurances && (
                      <Chip icon={<InsuranceIcon sx={{ fontSize: '14px !important' }} />}
                        label={`${procedureInsuranceRows.length} record${procedureInsuranceRows.length !== 1 ? 's' : ''}`} size="small"
                        sx={{ background: procedureInsuranceRows.length > 0 ? '#E7F5EC' : '#FDECEC', color: procedureInsuranceRows.length > 0 ? '#217A3C' : '#B23A3A', fontWeight: 600, fontSize: '0.7rem' }} />
                    )}
                  </Box>
                </Box>

                {/* Bulk edit bar — type once, apply to every ticked scheme. */}
                {selectedProcedure && procedureInsuranceRows.length > 0 && (
                  <BulkEditBar
                    fields={BULK_FIELDS}
                    values={bulkValues}
                    onChange={(k, v) => setBulkValues(prev => ({ ...prev, [k]: v }))}
                    selectedCount={selectedRowIds.length}
                    totalCount={procedureInsuranceRows.length}
                    onApply={handleBarApply}
                    onDelete={handleBarDelete}
                    applying={bulkApplying}
                    deleting={bulkDeleting}
                    itemLabel="scheme"
                  />
                )}

                {/* The row-as-template flow, kept as a secondary action — it
                    ADDS this procedure to schemes that do not have it yet,
                    which is a different job from editing existing rows. */}
                {selectedProcedure && procedureInsuranceRows.length > 0 && (
                  <Box sx={{ px: { xs: 1.5, sm: 2 }, py: 1, borderBottom: '1px solid #DDE5EF', display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', flexShrink: 0 }}>
                    <Button
                      size="small" variant="text" startIcon={<CopyIcon sx={{ fontSize: 15 }} />}
                      onClick={handleBulkApply}
                      disabled={selectedRowIds.length !== 1}
                      sx={{ textTransform: 'none', fontSize: 12, fontWeight: 700, color: '#123A6D', py: .3 }}
                    >
                      Copy one row to other schemes
                    </Button>
                    <Typography sx={{ fontSize: 11, color: '#93A2B8' }}>
                      {selectedRowIds.length === 1
                        ? 'Adds this procedure to schemes that do not have it yet'
                        : 'Tick exactly one row to use it as the template'}
                    </Typography>
                  </Box>
                )}

                {!selectedProcedure ? (
                  <EmptyState
                    icon={InsuranceIcon}
                    title="No procedure selected"
                    body="Click any procedure on the left to load its insurance records."
                  />
                ) : loadingProcedureInsurances ? (
                  <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Box textAlign="center">
                      <CircularProgress size={36} sx={{ color: '#2E9E4F' }} />
                      <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#93A2B8' }}>Loading records...</Typography>
                    </Box>
                  </Box>
                ) : procedureInsuranceRows.length === 0 ? (
                  <EmptyState
                    icon={InsuranceIcon}
                    title="No schemes on this procedure"
                    body={`Nothing is configured for ${selectedProcedure.procedure_} yet.`}
                  />
                ) : (
                  /*  minWidth was 1200, which put Actions past the right edge of
                      the panel on any normal laptop — so saving a row meant
                      scrolling sideways to find the button. 900 is a real floor
                      rather than a guess: below it the number inputs stop being
                      usable and scrolling is the honest answer, which is what a
                      phone gets. Above it the table simply fits. */
                  <TableContainer sx={{ flex: 1, overflowY: 'auto', overflowX: 'auto', '&::-webkit-scrollbar': { width: '6px', height: '6px' }, '&::-webkit-scrollbar-thumb': { background: '#DDE5EF', borderRadius: '10px' } }}>
                    <Table size="small" stickyHeader sx={{ minWidth: 900 }}>
                      <TableHead>
                        <TableRow>
                          {/*  Was a solid green band with white text — heavier than
                               the data under it, and a third green on a screen that
                               already has the brand green on its buttons. Quiet
                               uppercase labels on the canvas tint, the same head
                               every other converted admin table uses. */}
                          <TableCell sx={{ background: '#F4F7FB', borderBottom: '1px solid #DDE5EF', width: 48, textAlign: 'center', py: 1.25, px: 0.5 }}>
                            <Checkbox
                              size="small"
                              checked={selectedRowIds.length === procedureInsuranceRows.length && procedureInsuranceRows.length > 0}
                              indeterminate={selectedRowIds.length > 0 && selectedRowIds.length < procedureInsuranceRows.length}
                              onChange={handleSelectAllRows}
                              sx={{ color: '#93A2B8', '&.Mui-checked': { color: '#2E9E4F' }, '&.MuiCheckbox-indeterminate': { color: '#2E9E4F' }, p: 0.5 }}
                            />
                          </TableCell>
                          {['#', 'Insurance', 'Tariff (GH¢)', 'Top-up', 'Age', 'DRG Code', 'ICD-10', 'Specialty', 'Actions'].map(label => (
                            <TableCell key={label} sx={{ background: '#F4F7FB', color: '#93A2B8', fontWeight: 800, fontSize: '0.65rem', letterSpacing: '1px', textTransform: 'uppercase', borderBottom: '1px solid #DDE5EF', py: 1.25, whiteSpace: 'nowrap', ...(label === '#' && { width: 36, textAlign: 'center' }), ...(label === 'Actions' && { width: 96, textAlign: 'center' }) }}>
                              {label}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {procedureInsuranceRows.map((row, idx) => (
                          <EditableRow 
                            key={row.id || idx} 
                            row={row} 
                            idx={idx}
                            selected={selectedRowIds.includes(row.id)}
                            onSelectToggle={handleRowSelectionToggle}
                            onChange={(field, value) => handleRowChange(idx, field, value)}
                            onUpdate={handleUpdateRow} 
                            onDelete={handleDeleteRow}
                            updating={updatingRowId === row.id} 
                            deleting={deletingRowId === row.id} 
                          />
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Paper>
            </Box>
          </Box>
        </Box>
      </Paper>

      <NewProcedureDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        insuranceProviders={insuranceProviders}
        loadingInsurance={loadingInsurance}
        onSaved={loadProceduresFromBackend}
      />

      <BulkApplyDialog
        open={bulkApplyDialogOpen}
        onClose={() => {
          setBulkApplyDialogOpen(false);
          setSourceRowForBulkApply(null);
        }}
        sourceRow={sourceRowForBulkApply}
        insuranceProviders={insuranceProviders}
        loadingInsurance={loadingInsurance}
        onSaved={() => {
          if (selectedProcedure) {
            loadProcedureInsurancesForDisplay(selectedProcedure.id, selectedProcedure.procedure_);
          }
        }}
        existingStatuses={existingStatuses}
      />
    </>
  );
};

export default Procedure;